import axios from "axios";

import authService from "services/auth";

import { routes } from "routes/routes";
import { getUrlWithoutDomain } from "shared/functions";

import { store } from "store/configureStore";
import { setLoading } from "store/features/globalLoadingSlice";
import { setNotification } from "store/features/notificationSlice";
import { clearSession, refreshAuthToken } from "store/features/authSlice";

const anonymousEndpoint = [
  "api/auth/check-email",
  "api/auth/authenticate",
  "api/auth/register",
  "api/auth/login",
  "api/auth/forgot-password",
  "api/auth/reset-password",
  "api/auth/google-login",
  "api/auth/meta-login",
  "api/auth/refresh-token",
];

const errorIgnorePatterns = [
  /^api\/iban-qr\/[^/]+$/,
  /^api\/dynamic-invoice\/new-qr-invoice$/,
];

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

axios.interceptors.request.use((config) => {
  const clientVersion = process.env.REACT_APP_VERSION;
  if (clientVersion) {
    config.headers["X-Client-Version"] = clientVersion;
  }

  if (
    config &&
    !anonymousEndpoint.includes(getUrlWithoutDomain(config.url || ""))
  ) {
    const token = authService().getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const { url } = config;

    if (
      url?.includes("api/auth/logout") ||
      url?.includes("api/auth/refresh-token")
    ) {
      return Promise.reject(error);
    }

    if (
      response?.status === 401 &&
      config &&
      !config._retry &&
      !anonymousEndpoint.includes(getUrlWithoutDomain(config.url || ""))
    ) {
      store.dispatch(setLoading(true));

      const refreshToken = authService().getRefreshToken();
      const expiredAccessToken = authService().getAccessToken();

      if (!refreshToken || !expiredAccessToken) {
        store.dispatch(clearSession(routes.auth));
        store.dispatch(setLoading(false));
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            config.headers.Authorization = `Bearer ${newToken}`;
            resolve(axios(config));
          });
        });
      }

      config._retry = true;
      isRefreshing = true;

      try {
        const tokenResponse = await store
          .dispatch(refreshAuthToken({ refreshToken, expiredAccessToken }))
          .unwrap();

        const { accessToken, refreshToken: newRefreshToken } = tokenResponse;

        authService().setAccessToken(accessToken);
        authService().setRefreshToken(newRefreshToken);

        onRefreshed(accessToken);
        isRefreshing = false;

        config.headers.Authorization = `Bearer ${accessToken}`;

        return axios(config);
      } catch (err) {
        isRefreshing = false;

        store.dispatch(clearSession(routes.auth));
        return Promise.reject(err);
      } finally {
        store.dispatch(setLoading(false));
      }
    }

    const errorMessage = response?.data?.title;

    const shouldIgnore = errorIgnorePatterns.some((pattern) =>
      pattern.test(getUrlWithoutDomain(url || "")),
    );

    if (errorMessage && !shouldIgnore) {
      store.dispatch(
        setNotification({ severity: "error", message: errorMessage }),
      );
    }

    return Promise.reject(error);
  },
);
